import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Target, 
  Calendar, 
  Clock, 
  CheckSquare, 
  ArrowLeft,
  Save,
  TrendingUp,
  Users,
  Building2,
  ExternalLink,
  MessageSquare,
  AlertCircle
} from 'lucide-react';

const WeeklySummary = ({ 
  selectedTasks, 
  weeklySchedule, 
  onSave, 
  onPrevious,
  weekDates,
  dayNames 
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [weekNotes, setWeekNotes] = useState('');
  
  // טעינת הערות מ-localStorage
  useEffect(() => {
    const savedNotes = localStorage.getItem('currentWeeklyNotes');
    if (savedNotes) {
      try {
        const notesData = JSON.parse(savedNotes);
        setWeekNotes(notesData.notes || '');
      } catch (error) {
        console.error('Error loading weekly notes:', error);
      }
    }
  }, []);
  
  const getTotalScheduledTasks = () => {
    return Object.values(weeklySchedule).reduce((total, daySchedule) => {
      return total + Object.values(daySchedule).reduce((dayTotal, task) => {
        return dayTotal + (task ? 1 : 0);
      }, 0);
    }, 0);
  };

  const handleTaskClick = (taskId) => {
    navigate(`/tasks`, { state: { taskId } });
  };

  const getTasksByDay = () => {
    const tasksByDay = {};
    Object.keys(weeklySchedule).forEach(dateStr => {
      const daySchedule = weeklySchedule[dateStr] || {};
      let dayTotal = 0;
      Object.values(daySchedule).forEach(task => {
        if (task) {
          dayTotal++;
        }
      });
      tasksByDay[dateStr] = dayTotal;
    });
    return tasksByDay;
  };

  const getTasksByPriority = () => {
    const priorityCount = { high: 0, medium: 0, low: 0 };
    Object.values(weeklySchedule).forEach(daySchedule => {
      Object.values(daySchedule).forEach(task => {
        if (task) {
          if (task.priority === 'high') priorityCount.high++;
          else if (task.priority === 'medium') priorityCount.medium++;
          else priorityCount.low++;
        }
      });
    });
    return priorityCount;
  };

  const tasksByDay = getTasksByDay();
  const tasksByPriority = getTasksByPriority();
  const totalScheduled = getTotalScheduledTasks();
const handleSave = () => {

  try {
    console.log('Starting save from WeeklySummary...');
    
    if (totalScheduled === 0) {
      alert('אין משימות מתוכננות לשמירה. אנא תכנן זמנים למשימות לפני השמירה.');
      return;
    }
    
    const weeklyPlans = JSON.parse(localStorage.getItem('weeklyPlans') || '[]');
    const weekStartStr = weekDates[0].toISOString().split('T')[0];
    const existingPlanIndex = weeklyPlans.findIndex(plan => 
      plan.summaryData?.weekStart === weekStartStr && 
      plan.userId === currentUser?.id
    );
    
    // אם יש תכנון קיים
    if (existingPlanIndex >= 0) {
      const result = window.confirm(
        'כבר קיים תכנון לשבוע זה.\n\n' +
        'לחץ "אישור" כדי להוסיף את המשימות החדשות לתכנון הקיים\n' +
        'לחץ "ביטול" כדי לחזור ולערוך'
      );
      
      if (!result) {
        return;
      }
      
      // מיזוג התכנון החדש עם הקיים
      const existingPlan = weeklyPlans[existingPlanIndex];
      const mergedSchedule = { ...existingPlan.summaryData.schedule };
      
      // הוספת המשימות החדשות
      Object.keys(weeklySchedule).forEach(dateStr => {
        if (!mergedSchedule[dateStr]) {
          mergedSchedule[dateStr] = {};
        }
        Object.keys(weeklySchedule[dateStr]).forEach(timeSlot => {
          if (weeklySchedule[dateStr][timeSlot]) {
            // בדיקה אם השעה תפוסה
            if (mergedSchedule[dateStr][timeSlot]) {
              alert(`לא ניתן להוסיף משימה ב-${timeSlot} בתאריך ${dateStr} - השעה כבר תפוסה`);
            } else {
              mergedSchedule[dateStr][timeSlot] = weeklySchedule[dateStr][timeSlot];
            }
          }
        });
      });
      
      // עדכון התכנון הקיים
      existingPlan.summaryData.schedule = mergedSchedule;
      existingPlan.summaryData.updatedAt = new Date().toISOString();
      
      // הוספת ההערות החדשות אם יש
      if (weekNotes) {
        existingPlan.summaryData.weekNotes = 
          (existingPlan.summaryData.weekNotes || '') + '\n\n' + weekNotes;
      }
      
      weeklyPlans[existingPlanIndex] = existingPlan;
      
    } else {
      // יצירת תכנון חדש
      const summaryData = {
weekStart: weekDates[0].toISOString().split('T')[0],  // ← שים לב ל-split
weekEnd: weekDates[6].toISOString().split('T')[0],    // ← שים לב ל-split
        totalTasks: selectedTasks.length,
        scheduledTasks: totalScheduled,
        tasksByDay,
        tasksByPriority,
        schedule: weeklySchedule,
        weekNotes: weekNotes,
        createdAt: new Date().toISOString()
      };
      
      const newPlan = {
        userId: currentUser?.id,
        userName: currentUser?.name,
        savedAt: new Date().toISOString(),
        summaryData: summaryData,
        type: 'weekly_planning'
      };
      
      weeklyPlans.push(newPlan);
    }
    
    localStorage.setItem('weeklyPlans', JSON.stringify(weeklyPlans));
    
    // ניקוי ההערות
    localStorage.removeItem('currentWeeklyNotes');
    
    alert('התכנון השבועי נשמר/עודכן בהצלחה! 🎉');
    
    navigate('/dashboard');
    
  } catch (error) {
    console.error('Error in handleSave:', error);
    alert('שגיאה בשמירת התכנון השבועי: ' + error.message);
  }
};
  return (
    <div className="space-y-8">
      {/* Summary Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
          <Target className="w-6 h-6 text-green-600" />
          סיכום תכנון השבוע
        </h2>
        <p className="text-gray-600 text-lg">
          הנה סיכום של התכנון השבועי שלך. בדוק שהכל נראה טוב ושמור את התכנון.
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white">
              <CheckSquare className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold text-blue-600">{selectedTasks.length}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">סה"כ משימות</h3>
          <p className="text-sm text-gray-600">משימות שנבחרו לתכנון</p>
        </div>
        
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-xl text-white">
              <Calendar className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold text-green-600">{totalScheduled}</span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">משימות מתוכננות</h3>
          <p className="text-sm text-gray-600">משימות עם זמן מוגדר</p>
        </div>
        
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl text-white">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold text-purple-600">
              {selectedTasks.length > 0 ? Math.round((totalScheduled / selectedTasks.length) * 100) : 0}%
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">אחוז תכנון</h3>
          <p className="text-sm text-gray-600">משימות מתוכננות מהסה"כ</p>
        </div>
        
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl text-white">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold text-orange-600">
              {Math.round(totalScheduled / 7)}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">ממוצע יומי</h3>
          <p className="text-sm text-gray-600">משימות ליום בממוצע</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Tasks by Day */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl text-white">
              <Calendar className="w-5 h-5" />
            </div>
            התפלגות לפי ימים
          </h3>
          
          <div className="space-y-4">
            {weekDates.map((date, index) => {
              const dateStr = date.toISOString().split('T')[0];
              const taskCount = tasksByDay[dateStr] || 0;
              const percentage = selectedTasks.length > 0 ? (taskCount / selectedTasks.length) * 100 : 0;
              
              return (
                <div key={dateStr} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600">{date.getDate()}</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{dayNames[index]}</div>
                      <div className="text-sm text-gray-600">{taskCount} משימות</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-bold text-gray-700 w-12 text-right">
                      {Math.round(percentage)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tasks by Priority */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl text-white">
              <Target className="w-5 h-5" />
            </div>
            התפלגות לפי עדיפות
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-red-600">!</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">עדיפות גבוהה</div>
                  <div className="text-sm text-gray-600">{tasksByPriority.high} משימות</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-red-600">{tasksByPriority.high}</div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-yellow-600">~</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">עדיפות בינונית</div>
                  <div className="text-sm text-gray-600">{tasksByPriority.medium} משימות</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-yellow-600">{tasksByPriority.medium}</div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-600">-</span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">עדיפות נמוכה</div>
                  <div className="text-sm text-gray-600">{tasksByPriority.low} משימות</div>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-600">{tasksByPriority.low}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Schedule Overview */}
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white">
            <Calendar className="w-5 h-5" />
          </div>
          תצוגת התכנון השבועי
        </h3>
        
        <div className="grid grid-cols-7 gap-4">
          {weekDates.map((date, index) => {
            const dateStr = date.toISOString().split('T')[0];
            const daySchedule = weeklySchedule[dateStr] || {};
            const dayTasks = [];
            
            Object.values(daySchedule).forEach(task => {
              if (task) {
                dayTasks.push(task);
              }
            });
            
            return (
              <div key={dateStr} className="bg-gray-50 rounded-2xl p-4">
                <div className="text-center mb-4">
                  <div className="text-sm font-medium text-gray-600">{dayNames[index]}</div>
                  <div className="text-lg font-bold text-gray-900">{date.getDate()}</div>
                  <div className="text-xs text-gray-500">{dayTasks.length} משימות</div>
                </div>
                
                <div className="space-y-2">
                  {dayTasks.slice(0, 3).map((task, taskIndex) => (
                    <div 
                      key={taskIndex} 
                      className="bg-blue-100 text-blue-800 text-xs p-2 rounded cursor-pointer hover:bg-blue-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskClick(task.id);
                      }}
                    >
                      {task.title}
                    </div>
                  ))}
                  {dayTasks.length > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{dayTasks.length - 3} נוספות
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* שדה הערות ומטרות */}
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-800">הערות ומטרות לשבוע</h3>
          <span className="text-sm text-gray-500">(אופציונלי)</span>
        </div>
        <textarea
          value={weekNotes}
          onChange={(e) => setWeekNotes(e.target.value)}
          placeholder="רשום מטרות, הערות ותזכורות חשובות לשבוע...
לדוגמה:
- השבוע להתמקד בסגירת הפרויקט הגדול
- יום שלישי - ישיבת דירקטוריון חשובה
- מטרה שבועית: להגדיל פרודוקטיביות ב-20%"
          className="w-full h-32 px-4 py-3 border-2 border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        />
        <div className="text-sm text-gray-500 mt-2">
          💡 ההערות ישמרו יחד עם התכנון ותוכל לראות אותן בלוח הבקרה
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          className="px-8 py-4 bg-gray-500 text-white rounded-2xl hover:bg-gray-600 transition-all duration-300 font-bold text-lg flex items-center gap-3"
        >
          <ArrowLeft className="w-6 h-6" />
          חזור לתכנון זמנים
        </button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onSave) {
              onSave({
                weekStart: weekDates[0].toISOString(),
                weekEnd: weekDates[6].toISOString(),
                totalTasks: selectedTasks.length,
                scheduledTasks: totalScheduled,
                tasksByDay,
                tasksByPriority,
                schedule: weeklySchedule,
                weekNotes: weekNotes,
                createdAt: new Date().toISOString()
              });
            } else {
              handleSave();
            }
          }}
          className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 font-bold text-lg flex items-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <Save className="w-6 h-6" />
          שמור תכנון שבועי
        </button>
      </div>
    </div>
  );
};

export default WeeklySummary;